import type { CardUrlType } from '@blinq-types';
import styles from '@components/Card2/Card/Card.module.scss';
import { BasicUrlEditor } from '@components/Card2/CardFormFields/UrlFields/BasicUrlEditor/BasicUrlEditor';
import { PhoneNumberBasedUrlEditor } from '@components/Card2/CardFormFields/UrlFields/PhoneNumberBasedUrlEditor/PhoneNumberBasedUrlEditor';
import { CardItemEditor } from '@components/Card2/CardItemEditor';
import { canDeleteCardItem } from '@components/Card2/utils';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { useCardUrlFieldState } from '@hooks/use-card-field-state';
import useNode from '@hooks/use-node';
import { isCompleteURL, isPartialURL, isURLFieldValueValid } from '@modules/card-helpers';
import { logger } from '@modules/logger';
import { useContext, useMemo, useState } from 'react';

import type { RowProps } from '../../types';
import { getHref, getNewCardFieldLexorank, useCardFieldMutations } from '../../utils';
import { FieldEditor } from '../FieldEditor';
import { FieldRow } from '../FieldRow';
import UrlContent from './Content';
import type { CardField, ValueProvider } from '.cache/__types__';

export function Url({
  cardId,
  field,
  index,
  size,
  cardAttributesTypes,
  ...restProps
}: RowProps): JSX.Element {
  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();

  const cardFieldMutations = useCardFieldMutations(cardId, field);
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(
    null,
  );

  const { editPageMode, setPendingCardField, flavour } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  const fieldState = useCardUrlFieldState(field, 'member');

  const fieldNeedsValidUrl =
    fieldState.type === 'calendar' ||
    fieldState.type === 'company_url' ||
    fieldState.type === 'link' ||
    fieldState.type === 'linkedin' ||
    fieldState.type === 'threads';

  const isValidUrl = useMemo(() => {
    if (fieldState.type === 'linkedin') {
      return isURLFieldValueValid('linkedin', fieldState.shorthand);
    }

    return (
      (isPartialURL(fieldState.shorthand) || isCompleteURL(fieldState.shorthand)) &&
      isCompleteURL(fieldState.value)
    );
  }, [fieldState.shorthand, fieldState.type, fieldState.value]);

  const onSave = (newValueProvider?: ValueProvider) => {
    const activeAttribute = cardAttributesTypes.find((item) => item.name === fieldState.type);
    const attributeTypeId = activeAttribute?.id;
    const stage = fieldState.value ? 'done' : 'todo';

    const shouldDiscardShorthand =
      flavour === 'team' && fieldState.valueProvider === 'member' && !fieldState.value;

    const sharedMutationValues: Omit<CardField, 'id' | 'attribute_type_id'> & { card_id: string } =
      {
        card_id: cardId,
        label: fieldState.label,
        label_locked: fieldState.labelLocked,
        is_optional: fieldState.isOptional,
        value: fieldState.value,
        phone_country_code:
          fieldState.type === 'whatsapp' || fieldState.type === 'signal'
            ? fieldState.phoneCountryCode
            : null,
        value_provider: fieldState.isModified // Prevent updating the value provider if the user hasn't made any changes
          ? (newValueProvider ?? fieldState.valueProvider)
          : fieldState.valueProvider,
        value_locked: fieldState.valueLocked,
        instruction_text: fieldState.instruction ?? null,
        shorthand: shouldDiscardShorthand ? null : fieldState.shorthand,
        stage,
      };

    if (editPageMode === 'create') {
      if (!attributeTypeId) {
        logger.error('No attribute type id found for url field');
        return;
      }

      cardFieldMutations.create(
        {
          ...sharedMutationValues,
          lexorank: getNewCardFieldLexorank(cardId),
          attribute_type_id: attributeTypeId,
        },
        activeAttribute,
      );

      return;
    }

    if (!field.lexorank) {
      logger.error('No lexorank found for URL field');
      return;
    }

    cardFieldMutations.update({
      ...sharedMutationValues,
      lexorank: field.lexorank,
      id: field.id,
    });
  };

  const canDelete = canDeleteCardItem({
    flavour,
    isOptional: Boolean(field.is_optional),
    isTemplated: Boolean(field.template_id),
    valueProvider: field.value_provider,
  });

  const handleDelete = () => {
    if (!canDelete) return;
    cardFieldMutations.remove({
      card_id: cardId,
      id: field.id,
    });
  };

  const handleCancel = () => {
    fieldState.onCancel();
    setPendingCardField(undefined);
  };

  const validator = (): boolean => {
    if (!fieldNeedsValidUrl) {
      return true;
    }

    if (flavour === 'team' && fieldState.valueProvider === 'member') {
      return true;
    }

    if (flavour === 'template' && fieldState.valueProvider === 'member') {
      return true;
    }

    return isValidUrl;
  };

  if (mode === 'clickable') {
    return (
      <a href={getHref(field)} className={styles['card-item']} rel="noreferrer">
        <UrlContent
          label={field.label || ''}
          size={size}
          type={(field.attribute_type?.name as CardUrlType) || ''}
          labelPlaceholder={field.attribute_type?.display_name ?? undefined}
        />
      </a>
    );
  }

  if (mode === 'read-only') {
    return (
      <div className={styles['card-item']} rel="noreferrer" style={{ cursor: 'default' }}>
        <UrlContent
          label={field.label || ''}
          size={size}
          type={(field.attribute_type?.name as CardUrlType) || ''}
          labelPlaceholder={field.attribute_type?.display_name ?? undefined}
        />
      </div>
    );
  }

  return (
    <>
      <FieldRow
        id={field.id}
        fieldValue={field.value ?? ''}
        index={index}
        isLocked={fieldState.fullyLocked}
        isLockedTooltipMessage={fieldState.lockedTooltipMessage}
        isOptional={field.is_optional}
        fieldBtnRef={fieldBtnRef}
        fieldRef={fieldRef}
        {...restProps}
      >
        <div className={styles['card-item']} data-edit-mode>
          <UrlContent
            label={fieldState.label}
            size={size}
            // @ts-expect-error - TODO - fix type error
            type={fieldState.type}
            labelPlaceholder={field.attribute_type?.display_name ?? undefined}
          />
        </div>
      </FieldRow>

      <FieldEditor
        title={field.attribute_type?.display_name || ''}
        id={field.id}
        updateTempValueProviderForTour={setTempValueProviderForTour}
        fieldBtnNode={fieldBtnNode}
        fieldNode={fieldNode}
        hasUnsavedChanges={field.value !== fieldState.value}
        positionDeps={[]}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onSave={onSave}
        validator={validator}
        isFieldLinkedToProfile={fieldState.valueProvider === 'profile'}
        unlinkCardField={onSave}
        canDelete={canDelete}
      >
        <CardItemEditor
          {...fieldState}
          tempValueProviderForTour={tempValueProviderForTour}
          showLabelWhenIndividual
          fieldType="url"
        >
          {fieldState.type === 'whatsapp' || fieldState.type === 'signal' ? (
            <PhoneNumberBasedUrlEditor {...fieldState} />
          ) : (
            <BasicUrlEditor {...fieldState} />
          )}
        </CardItemEditor>
      </FieldEditor>
    </>
  );
}
